import { useEffect, useRef, useState } from 'react';
import { X, Maximize, Minimize } from 'lucide-react';
import { useNavigate } from 'react-router';

declare global {
	interface Window {
		embedpano?: any;
		removepano?: any;
		krpano?: any;
		navigateToDamage?: (assetId: string, inspectionId: string, damageId: string) => void;
	}
}

interface KrpanoViewerProps {
	onClose: () => void;
	startScene?: string;
}

export default function KrpanoViewer({ onClose, startScene }: KrpanoViewerProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [isScriptLoaded, setIsScriptLoaded] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const navigate = useNavigate();

	const TOUR_JS_PATH = '/krpano/tour.js';
	const TOUR_XML_PATH = '/krpano/tour.xml';
	const TARGET_ID = 'krpano-target-div-inline';

	useEffect(() => {
		if (!window.embedpano) {
			const script = document.createElement('script');
			script.src = TOUR_JS_PATH;
			script.async = true;
			script.onload = () => setIsScriptLoaded(true);
			document.body.appendChild(script);
		} else {
			setIsScriptLoaded(true);
		}
	}, []);

	// Bridge for Routing
	useEffect(() => {
		window.navigateToDamage = (assetId, inspectionId, damageId) => {
			if (assetId && inspectionId && damageId) {
				const navigateTo = () =>
					navigate(`/assets/${assetId}/inspections/${inspectionId}/damages/${damageId}`);

				if (document.fullscreenElement) {
					document
						.exitFullscreen()
						.then(navigateTo)
						.catch((err) => {
							console.error(err);
							navigateTo();
						});
				} else {
					navigateTo();
				}
			}
		};

		return () => {
			window.navigateToDamage = undefined;
		};
	}, [navigate]);

	// Initialize Krpano
	useEffect(() => {
		if (!isScriptLoaded || !window.embedpano) return;

		// Cleanup previous instances
		if (window.removepano) {
			try {
				window.removepano(TARGET_ID);
			} catch (e) {}
		}

		const timer = setTimeout(() => {
			window.embedpano({
				xml: TOUR_XML_PATH,
				target: TARGET_ID,
				html5: 'only',
				mobilescale: 1.0,
				passQueryParameters: 'startscene,startlookat',
				consolelog: true,
				bgcolor: '#000000',
				vars: startScene ? { startscene: startScene } : {},
				onready: (krpano: any) => {
					window.krpano = krpano;
					krpano.call(`set(isfullscreen, ${document.fullscreenElement ? 'true' : 'false'});`);
					if (startScene) krpano.call(`loadscene(${startScene}, null, MERGE);`);
				},
				onerror: (err: any) => console.error('Krpano Error:', err),
			});
		}, 100);

		return () => {
			clearTimeout(timer);
			if (window.removepano) {
				try {
					window.removepano(TARGET_ID);
				} catch (e) {}
			}
		};
	}, [isScriptLoaded, startScene]);

	const toggleFullscreen = () => {
		if (!containerRef.current) return;
		if (!document.fullscreenElement) {
			containerRef.current.requestFullscreen().catch((err) => console.error(err));
		} else {
			document.exitFullscreen();
		}
	};

	useEffect(() => {
		const handleFSChange = () => {
			setIsFullscreen(!!document.fullscreenElement);
		};
		document.addEventListener('fullscreenchange', handleFSChange);
		return () => document.removeEventListener('fullscreenchange', handleFSChange);
	}, []);

	return (
		<div ref={containerRef} className="w-full h-full relative bg-black flex flex-col">
			{/* Controls Bar */}
			<div className="absolute top-4 right-4 z-[100] flex gap-2">
				<button
					onClick={toggleFullscreen}
					className="bg-white/90 p-2 rounded-full shadow hover:bg-white transition-colors"
				>
					{isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
				</button>
				{!isFullscreen && (
					<button
						onClick={onClose}
						className="bg-white/90 p-2 rounded-full shadow hover:bg-white transition-colors"
					>
						<X size={20} />
					</button>
				)}
			</div>
			<div id={TARGET_ID} className="w-full h-full flex-1 bg-black" />
		</div>
	);
}
